import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const isCheckingRef = useRef(false);

  const checkAuth = useCallback(async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    
    try {
      // Get stored token for Authorization header fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        try {
          const subResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/check`, {
            credentials: 'include',
            headers
          });
          
          if (subResponse.ok) {
            const subData = await subResponse.json();
            setSubscription(subData);
          }
        } catch (e) {
          console.error('Subscription check error:', e);
        }
      } else {
        setUser(null);
        setSubscription(null);
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setSubscription(null);
    } finally {
      setLoading(false);
      isCheckingRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    console.log('AuthContext.login called with email:', email);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      console.log('Login response status:', response.status);
      
      // Clone response before reading to avoid "body already consumed" error
      // This handles cases where browser extensions or scripts read the body first
      const responseClone = response.clone();
      
      let data;
      try {
        // Try to read from original response first
        data = await response.json();
      } catch (originalError) {
        console.log('Original response read failed, trying clone:', originalError.message);
        try {
          // Fall back to cloned response
          data = await responseClone.json();
        } catch (cloneError) {
          console.error('Both response reads failed');
          // Last resort: try text parsing
          try {
            const textClone = response.clone();
            const text = await textClone.text();
            data = JSON.parse(text);
          } catch (textError) {
            console.error('Text fallback also failed:', textError);
            throw new Error('Failed to read server response. Please try again or clear your browser cache.');
          }
        }
      }
      
      console.log('Login response data:', data);
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Set user
      setUser(data);
      
      // Store session token in localStorage as backup for cross-domain cookie issues
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      
      // Set subscription
      const hasAccess = data.subscription_status === 'active' || data.subscription_status === 'trialing';
      setSubscription({
        has_access: hasAccess,
        subscription_status: data.subscription_status,
        trial_ends_at: data.trial_ends_at,
        subscription_ends_at: data.subscription_ends_at
      });
      
      sessionStorage.setItem('just_authenticated', 'true');
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      throw error;
    }
  };

  const register = async (email, password, name) => {
    console.log('AuthContext.register called with email:', email);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name })
      });
      
      console.log('Register response status:', response.status);
      
      // Clone response before reading to avoid "body already consumed" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (originalError) {
        console.log('Original response read failed, trying clone:', originalError.message);
        try {
          data = await responseClone.json();
        } catch (cloneError) {
          console.error('Both response reads failed');
          throw new Error('Failed to read server response. Please try again or clear your browser cache.');
        }
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Set user
      setUser(data);
      
      // Set subscription (new users start with trial)
      setSubscription({
        has_access: true,
        subscription_status: 'trialing',
        trial_ends_at: data.trial_ends_at
      });
      
      sessionStorage.setItem('just_authenticated', 'true');
      
      return data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const googleLogin = async (sessionId) => {
    console.log('AuthContext.googleLogin called');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      // Clone response before reading to avoid "body already consumed" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (originalError) {
        console.log('Original response read failed, trying clone:', originalError.message);
        try {
          data = await responseClone.json();
        } catch (cloneError) {
          console.error('Both response reads failed');
          throw new Error('Failed to read server response. Please try again.');
        }
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Google login failed');
      }
      
      // Set user
      setUser(data);
      
      // Set subscription
      const hasAccess = data.subscription_status === 'active' || data.subscription_status === 'trialing';
      setSubscription({
        has_access: hasAccess,
        subscription_status: data.subscription_status,
        trial_ends_at: data.trial_ends_at,
        subscription_ends_at: data.subscription_ends_at
      });
      
      sessionStorage.setItem('just_authenticated', 'true');
      
      return data;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setSubscription(null);
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('just_authenticated');
  };

  const hasAccess = () => {
    return subscription?.has_access === true;
  };

  // Get session token from localStorage
  const getSessionToken = useCallback(() => {
    return localStorage.getItem('session_token');
  }, []);

  const value = {
    user,
    loading,
    subscription,
    sessionToken: localStorage.getItem('session_token'),
    getSessionToken,
    login,
    register,
    googleLogin,
    logout,
    checkAuth,
    hasAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
